/**
 * ChunkErrorBoundary - lapie ChunkLoadError + auto-reload.
 *
 * Wzorzec z v1 historii: po deploy na Vercel stare URL-e chunk JS wygasaja.
 * User z cachem starego HTML probuje pobrac nieistniejacy plik -> ChunkLoadError ->
 * React crashuje calej aplikacji.
 *
 * Strategia:
 *   1. Lapie error w boundary
 *   2. Sprawdza czy to ChunkLoadError (po name + message patterns)
 *   3. Jezeli tak + JEST SIEC + nie probowalismy juz reload (sessionStorage flag) ->
 *      auto-reload (instant fresh HTML + chunks)
 *   4. Jezeli nie ChunkLoadError lub po reload dalej fail -> fallback UI z manual reload button
 *
 * Dlaczego punkt 3 sprawdza sieć: ten sam ChunkLoadError leci, gdy telefon
 * stracił zasięg i nie potrafi dociągnąć kawałka aplikacji dla sekcji, w którą
 * user właśnie wszedł. Przeładowanie bez sieci trafia w ERR_INTERNET_DISCONNECTED
 * i kasuje aplikację razem z tym, co user miał na ekranie — zamiast komunikatu
 * zostaje pusta strona. Zmierzone: wejście w nieodwiedzoną sekcję offline gubiło
 * 6 chunków, resetowało trasę i zostawiało 0 znaków treści.
 */
import { Component, type ReactNode } from "react";

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === "ChunkLoadError" ||
    error.message.includes("Loading chunk") ||
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Importing a module script failed")
  );
}

/** `navigator.onLine === false` to jedyny pewny sygnał; brak API traktujemy jak sieć. */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Czy wolno przeładować stronę po błędzie ładowania kawałka aplikacji.
 *
 * Wydzielone z komponentu, bo to jedyna nietrywialna decyzja w tym pliku, a jej
 * pomyłka kasuje aplikację użytkownikowi. Reguła: przeładowanie leczy WYGASŁY
 * chunk po deployu, ale bez sieci trafia w ERR_INTERNET_DISCONNECTED i zostawia
 * pustą stronę — wtedy trzeba poczekać na powrót połączenia.
 */
export function shouldReloadAfterChunkError(params: {
  isChunkError: boolean;
  offline: boolean;
  alreadyTried: boolean;
}): boolean {
  const { isChunkError, offline, alreadyTried } = params;
  return isChunkError && !offline && !alreadyTried;
}

const RELOAD_FLAG = "adresflow_v2_chunk_reload_attempted";

interface State {
  hasError: boolean;
  isChunkError: boolean;
  wasOffline: boolean;
}

export class ChunkErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, isChunkError: false, wasOffline: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isChunkError: isChunkLoadError(error), wasOffline: isOffline() };
  }

  componentDidCatch(error: unknown): void {
    const isChunkError = isChunkLoadError(error);
    if (!isChunkError) return;

    const offline = isOffline();
    if (offline) {
      // Bez sieci przeładowanie tylko pogarsza sprawę — czekamy, aż wróci.
      window.addEventListener("online", this.reload, { once: true });
      return;
    }
    if (shouldReloadAfterChunkError({ isChunkError, offline, alreadyTried: !!sessionStorage.getItem(RELOAD_FLAG) })) {
      sessionStorage.setItem(RELOAD_FLAG, "1");
      window.location.reload();
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener("online", this.reload);
  }

  private reload = (): void => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      // Clear flag po udanym render - następny chunk error znow trigger reload
      sessionStorage.removeItem(RELOAD_FLAG);
      return this.props.children;
    }

    if (this.state.isChunkError && this.state.wasOffline) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md text-center space-y-4">
            <h2 className="font-display text-2xl font-bold">Brak połączenia</h2>
            <p className="text-muted-foreground">
              Ta część aplikacji nie zdążyła się pobrać, zanim zniknął internet. Sprawdź
              połączenie — wrócimy tu sami, gdy tylko się pojawi.
            </p>
            <button
              onClick={this.reload}
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center space-y-4">
          {this.state.isChunkError ? (
            <p className="text-muted-foreground">Ładowanie nowej wersji…</p>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold">Coś poszło nie tak</h2>
              <p className="text-muted-foreground">
                Spróbuj odświeżyć stronę. Jeśli błąd się powtarza, wyczyść cache przeglądarki.
              </p>
              <button
                onClick={this.reload}
                className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
              >
                Odśwież stronę
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}
